/**
 * useHoldIdleWhileAnswering (issue #29).
 *
 * Keeps the wall-tablet idle timer deferred for as long as the assistant turn
 * is worth looking at.  UI-SPEC §"Idle-mode suppression" asked for this in
 * Plan 05-07a and it was never built: `<DashboardRoute />` owns the 60s timer
 * and `<AssistantRoute />` is a child route under it, so before #29 the
 * kitchen tablet faded to the ambient hub in the middle of a streaming reply.
 *
 * Policy (deliberately here, not in the store — `DashboardStore` stays a dumb
 * counter that knows nothing about streams):
 *
 *   'streaming'                      → hold, no time limit of our own
 *   'done' | 'error' | 'cancelled'   → hold for GRACE_MS, then release
 *   'idle'                           → no hold
 *
 * All three terminal statuses leave text on screen worth reading, which is why
 * none of them releases immediately:
 *   - 'done'      the finished answer
 *   - 'error'     `AssistantRoute` keeps the interrupted half-answer on screen
 *                 rather than letting it vanish (see its `streamingMessage`)
 *   - 'cancelled' the partial text is snapshotted into the thread on Stop
 *
 * GRACE_MS is a reading allowance, not a second idle timeout: the ordinary 60s
 * countdown restarts whole once the hold releases (`useIdleTimer` restarts on
 * the release edge), so the worst case after a reply lands is 45s + 60s of lit
 * screen with nobody touching the tablet.
 *
 * Unmount releases immediately — navigating away mid-stream should not leave a
 * hold behind — because the hold lives in a `useIdleHold` effect and the grace
 * timer is cleared by this hook's own effect cleanup.
 *
 * The `holding` state is a LATCH, and it is load-bearing — do not collapse it
 * into a single acquire/release effect keyed on `status`.  Across
 * `streaming → done` it stays `true` unchanged, so `useIdleHold`'s `[active]`
 * dependency does not re-run: the hold is one continuous reference rather than
 * a release-then-reacquire, which is what keeps `idleHeldSince` on its first
 * stamp for the whole conversation.  `idleHoldDeadman.test.ts` asserts exactly
 * that.
 */
import { useEffect, useState } from 'react';
import { useIdleHold } from '../../lib/useIdleHold';
import type { StreamState } from './useAssistantStream';

/** How long a finished / failed / cancelled turn stays readable. */
const GRACE_MS = 45_000;

export function useHoldIdleWhileAnswering(status: StreamState['status']): void {
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    // Anything but 'idle' is worth looking at, so the hold is simply
    // "status !== 'idle'".  Only the release differs: 'idle' releases now,
    // terminal statuses release after the grace window.
    setHolding(status !== 'idle');
    if (status === 'idle' || status === 'streaming') return undefined;
    // Terminal: 'done' | 'error' | 'cancelled'.  Keyed on `status`, so the
    // grace window restarts (and the previous timer is cleared) on every
    // transition, and an unmount inside the window clears it too.
    const id = setTimeout(() => setHolding(false), GRACE_MS);
    return () => clearTimeout(id);
  }, [status]);

  useIdleHold(holding);
}
